/**
 * Table preferences that survive a reload.
 *
 * Held in `localStorage` rather than in component state alone: a reviewer who works through
 * nominations daily configures page size and columns once, and losing that on every refresh
 * would defeat the point of offering the choice. Deliberately NOT in the URL — these are one
 * person's lasting preference, not part of what a link should carry.
 *
 * Every read is defensive, because the stored value is outside this code's control: a user can
 * edit it, a previous version of the app may have written a different shape, and a browser can
 * refuse `localStorage` outright (Safari in private mode throws on access, not just on write).
 * A bad or missing value falls back to the defaults, so the worst case is a reset rather than a
 * page that will not render.
 *
 * Generic over the preferences shape so the type stays the caller's — this module only knows
 * how to persist an object, not what is in it.
 */

/** Namespaced so one page's preferences cannot collide with another's. */
export function preferencesKey(page: string): string {
  return `eventHub:${page}:preferences`;
}

/**
 * Reads stored preferences, merged over `defaults`.
 *
 * Merged rather than returned as-is: a stored value written before a preference existed would
 * otherwise leave that preference undefined, and the table would read it as "off" rather than
 * as "never chosen". A shallow merge is enough — the shape is one level of scalars plus
 * `contentDisplay`, which is replaced wholesale because a partial column list is meaningless.
 */
export function loadPreferences<T extends object>(page: string, defaults: T): T {
  try {
    const raw = localStorage.getItem(preferencesKey(page));
    if (!raw) return defaults;
    const parsed: unknown = JSON.parse(raw);
    // Arrays and null are objects to `typeof`; neither is a preferences object.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return defaults;
    }
    return { ...defaults, ...(parsed as Partial<T>) };
  } catch {
    /* Unreadable, unparseable, or localStorage unavailable. The defaults are always a valid
       answer, so there is nothing to report to the user. */
    return defaults;
  }
}

/**
 * Stores preferences, ignoring a failure.
 *
 * A quota error or a blocked `localStorage` must not interrupt the interaction that triggered
 * it: the preference still applies to this session, and the only thing lost is that it will
 * not be there next time. Telling the user their column choice could not be saved forever is
 * noise next to the thing they were actually doing.
 */
export function savePreferences(page: string, preferences: object): void {
  try {
    localStorage.setItem(preferencesKey(page), JSON.stringify(preferences));
  } catch {
    // Ignored on purpose — see above.
  }
}
